import { useTranslation } from 'react-i18next';

import { Button, Heading } from '@app/shared/ui/atoms/Common';
import { InfoRow } from '@app/shared/ui/molecules/Common';
import { StatusButton } from '@app/shared/ui/atoms/Admin/Withdrawals';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { useCancelWithdrawalMutation } from '@app/store/slices/withdrawal/withdrawalApi';
import { formatBalance } from '@app/shared/utils/common';
import notification from '@app/shared/utils/notification';
import { WithdrawalModel, WithdrawalStatus } from '@app/store/slices/withdrawal/withdrawalTypes';
import { PriceType } from '@app/shared/types/common';

type Props = { withdrawal: WithdrawalModel };

export function WithdrawalCancel({ withdrawal }: Props) {
  const { t } = useTranslation();
  const [cancelWithdrawal, { isLoading: isWithdrawalCancelling }] = useCancelWithdrawalMutation();
  const { handleFetchError } = useApiErrorHandler();

  const canCancel = withdrawal.status === WithdrawalStatus.PENDING && !isWithdrawalCancelling;

  const handleCancel = async () => {
    if (!canCancel) {
      return;
    }

    try {
      await cancelWithdrawal({
        body: { withdrawalId: withdrawal.id },
        requestedAmount: Number(withdrawal.requestedAmount),
      }).unwrap();
      notification.success(t('withdrawal.cancel_success_message'));
    } catch (err) {
      handleFetchError(err);
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2">
      <Heading level={3}>{t('withdrawal.submitted')}</Heading>

      <div>
        <InfoRow
          label={t('withdrawal.status')}
          value={<StatusButton variant={withdrawal.status} as="span" className="py-0.5" />}
          isEqual
        />
        <InfoRow
          label={t('withdrawal.amount_label')}
          value={<b>{formatBalance(PriceType.TON, withdrawal.requestedAmount)}</b>}
          isEqual
        />
        <InfoRow
          label={t('withdrawal.fee_label')}
          value={<b>{formatBalance(PriceType.TON, withdrawal.fee)}</b>}
          isEqual
        />
        <InfoRow
          label={t('withdrawal.payout_amount')}
          value={<b>{formatBalance(PriceType.TON, withdrawal.payoutAmount)}</b>}
          isEqual
        />
        <InfoRow
          label={t('withdrawal.address_label')}
          value={<b className="truncate">{withdrawal.address}</b>}
          isEqual
          withBottomBorder={false}
        />
      </div>

      <Button disabled={!canCancel} onClick={handleCancel}>
        {t('withdrawal.cancel_button')}
      </Button>
    </div>
  );
}
